import type { QueueModeManager } from "./queueModeManager"

const ACTIVITY_EVENTS: ReadonlyArray<keyof DocumentEventMap> = ["keydown", "pointerdown", "pointermove", "wheel"]

export function installQueueModeLifecycle(manager: QueueModeManager): () => void {
    if (typeof window === "undefined" || typeof document === "undefined") return () => undefined

    let windowFocused = document.hasFocus()
    let documentVisible = document.visibilityState === "visible"
    const syncFocus = () => manager.setAppFocused(windowFocused && documentVisible)
    const handleFocus = () => {
        windowFocused = true
        syncFocus()
    }
    const handleBlur = () => {
        windowFocused = false
        syncFocus()
    }
    const handlePageShow = () => {
        windowFocused = document.hasFocus()
        documentVisible = document.visibilityState === "visible"
        syncFocus()
    }
    const handlePageHide = () => {
        documentVisible = false
        syncFocus()
    }
    const handleVisibilityChange = () => {
        documentVisible = document.visibilityState === "visible"
        syncFocus()
    }
    let lastActivityForwardedAtMs = 0
    const handleActivity = (event: Event) => {
        if (!manager.isActive) return
        if (event.type === "keydown" || event.type === "pointerdown" || event.type === "pointerup") manager.prepareSounds()
        const nowMs = Date.now()
        if (nowMs - lastActivityForwardedAtMs < 1_000) return
        lastActivityForwardedAtMs = nowMs
        manager.recordUserActivity(nowMs)
    }

    syncFocus()
    window.addEventListener("focus", handleFocus)
    window.addEventListener("blur", handleBlur)
    window.addEventListener("pageshow", handlePageShow)
    window.addEventListener("pagehide", handlePageHide)
    document.addEventListener("visibilitychange", handleVisibilityChange)
    for (const eventName of ACTIVITY_EVENTS) document.addEventListener(eventName, handleActivity, { passive: true })
    document.addEventListener("pointerup", handleActivity, { passive: true })

    return () => {
        window.removeEventListener("focus", handleFocus)
        window.removeEventListener("blur", handleBlur)
        window.removeEventListener("pageshow", handlePageShow)
        window.removeEventListener("pagehide", handlePageHide)
        document.removeEventListener("visibilitychange", handleVisibilityChange)
        for (const eventName of ACTIVITY_EVENTS) document.removeEventListener(eventName, handleActivity)
        document.removeEventListener("pointerup", handleActivity)
        manager.setAppFocused(false)
    }
}
